/**
 * useDisclosure Hook
 * State management for open/close interactions
 */

import { useState, useCallback } from 'react';

/**
 * useDisclosure Hook
 * Manages boolean state for modals, drawers, dialogs, etc.
 * 
 * @param {object} options - Configuration options
 * @param {boolean} options.defaultIsOpen - Initial open state
 * @param {function} options.onOpen - Callback when opening
 * @param {function} options.onClose - Callback when closing
 * @param {string} options.id - Optional ID for accessibility
 * 
 * @returns {{ isOpen: boolean, onOpen: function, onClose: function, onToggle: function, getButtonProps: function, getDisclosureProps: function }}
 * 
 * @example
 * const { isOpen, onOpen, onClose } = useDisclosure();
 * 
 * return (
 *   <>
 *     <Button onClick={onOpen}>Open Modal</Button>
 *     <Modal isOpen={isOpen} onClose={onClose}>...</Modal>
 *   </>
 * );
 */
export const useDisclosure = ({
  defaultIsOpen = false,
  onOpen: onOpenProp,
  onClose: onCloseProp,
  id,
} = {}) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);

  const onOpen = useCallback(() => {
    setIsOpen(true);
    onOpenProp?.();
  }, [onOpenProp]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    onCloseProp?.();
  }, [onCloseProp]);

  const onToggle = useCallback(() => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }, [isOpen, onOpen, onClose]);

  /**
   * Get props for the trigger button
   */
  const getButtonProps = useCallback((props = {}) => ({
    ...props,
    onClick: (e) => {
      props.onClick?.(e);
      onToggle();
    },
    'aria-expanded': isOpen,
    'aria-controls': id,
  }), [isOpen, onToggle, id]);

  /**
   * Get props for the disclosure content
   */
  const getDisclosureProps = useCallback((props = {}) => ({
    ...props,
    id,
    hidden: !isOpen,
  }), [isOpen, id]);

  return {
    isOpen,
    onOpen,
    onClose,
    onToggle,
    getButtonProps,
    getDisclosureProps,
  };
};

export default useDisclosure;
